import React from 'react'

import { useTheme } from '@mui/material'
import { Input } from '@repo/ui'
import { SearchIcon } from 'lucide-react'

import { FIELD_RADIUS_MAX, fieldBorderRest, fieldFocusSx } from './fieldMetrics'

export interface ToolbarSearchFieldProps {
  /**
   * Fires with the current text, DEBOUNCED when `debounceMs` is set.
   *
   * The field is uncontrolled, so a parent that only feeds this into a query
   * does not re-render per keystroke.
   */
  onChange: (value: string) => void
  placeholder?: string
  /**
   * Accessible name. The placeholder is not one: it disappears the moment
   * anything is typed, and several browsers do not expose it as a name at all.
   */
  ariaLabel?: string
  /**
   * Milliseconds to wait after the last keystroke before calling `onChange`.
   *
   * 0 (the default) calls straight through — that is what a client-side filter
   * over already-loaded rows wants, since there is no request to spare. Set it
   * for a SERVER-side search, where every keystroke would otherwise be a
   * request and the in-flight ones can land out of order.
   */
  debounceMs?: number
  sx?: object
}

/**
 * The app's toolbar search field: one `@repo/ui` `Input` in one treatment.
 *
 * Extracted from the certificates `HeaderBar` on its SECOND use
 * (working-conventions §36) — the Activity feed needed the same field, and the
 * treatment is ~25 lines of `sx` carrying four decisions that are individually
 * easy to get subtly wrong and impossible to notice drifting: the 48px height
 * that matches the toolbar controls beside it, the clamped field radius, the
 * neutral (non-brand) focus, and the 16px font.
 *
 * This mirrors what `toolbarControlSx` in `fieldMetrics` already did for the
 * BUTTONS in the same row, and for the same reason. It is a component rather
 * than another exported `sx` because the leading magnifier and the debounce are
 * part of the control and an `sx` cannot carry either — the note on
 * `toolbarControlSx` records exactly that gap ("the glyph is half the mismatch,
 * and a shared `sx` cannot carry it").
 */
export const ToolbarSearchField: React.FC<ToolbarSearchFieldProps> = ({
  onChange,
  placeholder = 'Search',
  ariaLabel,
  debounceMs = 0,
  sx,
}) => {
  // Resolved here rather than via an `sx` callback — see the note on `sx` below.
  const theme = useTheme()

  /*
   * Held in a ref, not state: the timer is not rendered, and putting it in
   * state would re-render on every keystroke — which is the cost the debounce
   * exists to avoid.
   */
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  /*
   * The LATEST onChange, read at fire time.
   *
   * A debounced call runs against whatever this holds when the timer elapses,
   * so a parent that passes an inline arrow (all of them do) cannot leave a
   * stale closure queued — and there is no dependency array to keep in sync,
   * which is the other way this goes wrong.
   */
  const onChangeRef = React.useRef(onChange)
  onChangeRef.current = onChange

  React.useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    []
  )

  const handleChange = (value: string) => {
    if (!debounceMs) {
      onChange(value)
      return
    }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => onChangeRef.current(value), debounceMs)
  }

  return (
    <Input
      onChange={handleChange}
      placeholder={placeholder}
      inputProps={ariaLabel ? { 'aria-label': ariaLabel } : undefined}
      // ⚠️ A PLAIN OBJECT, never `sx={theme => …}`. `@repo/ui`'s Input merges with
      // `sx={{ ...readOnlySx, ...(sx || {}) }}` — an object spread — so a callback
      // form spreads to `{}` and is silently dropped at runtime.
      sx={{
        height: '48px',
        marginTop: '0px !important',
        borderRadius: `${FIELD_RADIUS_MAX}px`,
        // The shared control border — the tenant's Studio "Border color", not a
        // private copy of anything. See `fieldBorderRest`, which also records what
        // moving off the fixed 3:1 grey costs this field specifically.
        border: `1px solid ${fieldBorderRest(theme)} !important`,
        // 16px, not the inherited 14px. Below 16px iOS Safari zooms the whole
        // page when the field takes focus, which on a phone reads as the layout
        // lurching; 16px also just carries better at arm's length.
        '& input': { fontSize: '16px' },
        '& input::placeholder': { fontSize: '16px', opacity: 1, color: '#7E7E7E' },
        // The tenant's accent, NOT `@repo/ui`'s brand-purple fallback — in this
        // app `brand[*]` is derived from the tenant's own colour and degrades to
        // black on Vera's unthemed surface, so this cannot assert a colour
        // nobody chose. See `fieldAccent`.
        ...fieldFocusSx(theme),
        ...(sx || {}),
      }}
      startAdornment={
        <div className='w-[24px] flex items-center justify-center'>
          <SearchIcon color='#7E7E7E' size={24} />
        </div>
      }
    />
  )
}

export default ToolbarSearchField
